import type { Tag } from "@generated/prisma/client"
import type { IsRowFilterable } from "ag-grid-community"
import { first, isEqual, uniqBy } from "lodash"

import { TagsCell } from "~/components/planning/Tags/TagsCell"
import { AggregationMethod } from "~/components/reusable/AggregationSelector"
import { Badge, StatusBadge } from "~/components/design-system/components"
import { CellUser } from "~/components/reusable/CellUser"
import type { IVariantSummary } from "~/hooks/useVariantTableData"
import {
  createSizeDimensionColumn,
  createSizeSplitConfidenceColumn,
  createStringColumn,
  createUniqueStringColumn,
} from "~/utils/columnTemplates"
import { sizeValueFormatter, uniqueValueFormatter } from "~/utils/formatting"
import { formatVariantId } from "~/utils/helpers"
import { formatName } from "~/utils/namingUtils"
import {
  calculateStockStatusOnAggregatedLevel,
  stockStatusColor,
  stockStatusText,
} from "~/utils/outOfStockDatesUtils"
import {
  extractVariantSummaries,
  ProductTableColumn,
  translateProductType,
} from "~/utils/productTableUtils"
import { SIZE_DIMENSION_1_LABEL, SIZE_DIMENSION_2_LABEL } from "~/utils/sizeDimensions"
import { sortSizesComparator } from "~/utils/sizeSplit"
import { filterDefined, uniq } from "~/utils/tsUtils"

import type { ColumnSpec } from "./types"

export interface IdentityColumnCtx {
  /** Set filters are opt-in per table; `false` leaves the column unfiltered. */
  includeFilter: boolean
  aggregations: string[]
  /** `false` when filters apply to leaf rows, which is the only case that can filter groups. */
  groupAggFiltering: boolean | IsRowFilterable | undefined
}

type IdentitySpec = ColumnSpec<IVariantSummary, IdentityColumnCtx>

/** Group cells hold every leaf's value, so they collapse to a count once past one. */
function formatOneOrMany(
  params: { node?: { group?: boolean } | null; value?: any },
  plural: string,
  formatOne: (value: any) => any = (value) => value,
) {
  if (!params.node?.group) return formatOne(params.value)
  return params.value?.length > 1
    ? `${params.value?.length} ${plural}`
    : formatOne(first(params.value))
}

/** Sizes in the tenant's configured size order, de-duplicated across the variant's SKUs. */
export function sortedSizes(row: IVariantSummary): string[] {
  // Mutates row.sizes.
  return uniq(row.sizes.sort((a, b) => sortSizesComparator(a, b)))
}

export const PRODUCT_NAME_COLUMN: IdentitySpec = {
  colId: ProductTableColumn.ProductName,
  colDef: () =>
    createUniqueStringColumn<IVariantSummary>({
      colId: ProductTableColumn.ProductName,
      tooltipTitle: "Products",
      field: "productId",
      sortingField: "productName",
      sortingType: "string",
      rowGroup: true,
      rowGroupIndex: 9998, // Make sure this and variant always appears as the final grouping.
      hide: false,
    }),
}

export const PRODUCT_COLUMN: IdentitySpec = {
  colId: ProductTableColumn.Product,
  colDef: () =>
    createUniqueStringColumn<IVariantSummary>({
      colId: ProductTableColumn.Product,
      field: "productName",
      tooltipTitle: "Products",
    }),
}

export const PRODUCT_ID_COLUMN: IdentitySpec = {
  colId: ProductTableColumn.ProductId,
  colDef: () =>
    createUniqueStringColumn<IVariantSummary>({
      colId: ProductTableColumn.ProductId,
      field: "productId",
      tooltipTitle: "Product IDs",
      uniqueSuffix: "IDs",
      isIdentifier: true,
      useValueFormatterForExport: true,
    }),
}

export const VARIANT_ID_COLUMN: IdentitySpec = {
  colId: ProductTableColumn.VariantId,
  colDef: () =>
    createUniqueStringColumn<IVariantSummary>({
      colId: ProductTableColumn.VariantId,
      field: "variantId",
      tooltipTitle: "Variants",
      isIdentifier: true,
      useValueFormatterForExport: true,
      valueFormatter: (params) => formatOneOrMany(params, "IDs", formatVariantId),
    }),
}

export const VARIANT_NAME_COLUMN: IdentitySpec = {
  colId: ProductTableColumn.VariantName,
  colDef: () =>
    createUniqueStringColumn<IVariantSummary>({
      colId: ProductTableColumn.VariantName,
      field: "variantName",
      tooltipTitle: "Variant Names",
      useValueFormatterForExport: true,
      valueFormatter: (params) => formatOneOrMany(params, "variant names"),
    }),
}

export const VARIANT_COLUMN: IdentitySpec = {
  colId: ProductTableColumn.Variant,
  colDef: () =>
    createUniqueStringColumn<IVariantSummary>({
      colId: ProductTableColumn.Variant,
      field: "variantId",
      rowGroup: false,
      rowGroupIndex: 9999, // Make sure this and variant always appear as the final grouping.
      tooltipTitle: "Variants",
      valueFormatter: (params) => formatOneOrMany(params, "variants", formatVariantId),
      sortingField: "productName",
      sortingType: "string",
      hide: true,
      isIdentifier: true,
      useValueFormatterForExport: true,
    }),
}

export const SKU_COLUMN: IdentitySpec = {
  colId: ProductTableColumn.SKU,
  colDef: () =>
    createUniqueStringColumn<IVariantSummary>({
      colId: ProductTableColumn.SKU,
      field: "skuIds",
      valueFormatter: (params) => uniqueValueFormatter(params, "SKUs"),
      tooltipTitle: "SKUs",
      uniqueSuffix: "SKUs", // Since the tooltipTitle gets lowercased in the uniqueValueFormatter
      isIdentifier: true,
      useValueFormatterForExport: true,
    }),
}

export const COLOR_COLUMN: IdentitySpec = {
  colId: ProductTableColumn.Color,
  colDef: (ctx) =>
    createUniqueStringColumn<IVariantSummary>({
      colId: ProductTableColumn.Color,
      field: "color",
      tooltipTitle: "Colors",
      filter: ctx.includeFilter,
      sortingOrder: ["desc", "asc", null],
    }),
}

export const BASE_COLOR_COLUMN: IdentitySpec = {
  colId: ProductTableColumn.BaseColor,
  colDef: (ctx) =>
    createUniqueStringColumn<IVariantSummary>({
      colId: ProductTableColumn.BaseColor,
      field: "baseColor",
      tooltipTitle: "Base Colors",
      filter: ctx.includeFilter,
      sortingOrder: ["desc", "asc", null],
    }),
}

export const SIZE_COLUMN: IdentitySpec = {
  colId: ProductTableColumn.Size,
  calculate: sortedSizes,
  colDef: () =>
    createUniqueStringColumn<IVariantSummary>({
      colId: ProductTableColumn.Size,
      sortingOrder: ["desc", "asc", null],
      tooltipTitle: "Sizes",
      valueFormatter: (params) => sizeValueFormatter(params),
    }),
}

function sizeDimensionColumn(
  colId: ProductTableColumn.SizeDimension1 | ProductTableColumn.SizeDimension2,
  tooltipTitle: string,
): IdentitySpec {
  return {
    colId,
    colDef: (ctx) =>
      createSizeDimensionColumn<IVariantSummary>({
        colId,
        tooltipTitle,
        filter: ctx.includeFilter,
      }),
  }
}

export const SIZE_DIMENSION_1_COLUMN = sizeDimensionColumn(
  ProductTableColumn.SizeDimension1,
  SIZE_DIMENSION_1_LABEL,
)
export const SIZE_DIMENSION_2_COLUMN = sizeDimensionColumn(
  ProductTableColumn.SizeDimension2,
  SIZE_DIMENSION_2_LABEL,
)

export const SIZE_SPLIT_CONFIDENCE_COLUMN: IdentitySpec = {
  colId: ProductTableColumn.SizeSplitConfidence,
  colDef: (ctx) =>
    createSizeSplitConfidenceColumn<IVariantSummary>({
      colId: ProductTableColumn.SizeSplitConfidence,
      isHigherAggregation:
        ctx.aggregations[0] !== AggregationMethod.SKU &&
        ctx.aggregations[0] !== AggregationMethod.Variant,
      getEntries: (node) => filterDefined(node.allLeafChildren?.map((n) => n.data) ?? [node.data]),
    }),
}

export const BRAND_COLUMN: IdentitySpec = {
  colId: ProductTableColumn.Brand,
  colDef: () =>
    createUniqueStringColumn<IVariantSummary>({
      colId: ProductTableColumn.Brand,
      field: "brand",
      tooltipTitle: "Brands",
    }),
}

export const CATEGORY_COLUMN: IdentitySpec = {
  colId: ProductTableColumn.Category,
  colDef: (ctx) =>
    createUniqueStringColumn<IVariantSummary>({
      colId: ProductTableColumn.Category,
      field: "category",
      tooltipTitle: "Categories",
      filter: ctx.includeFilter,
    }),
}

export const GENDER_COLUMN: IdentitySpec = {
  colId: ProductTableColumn.Gender,
  colDef: () =>
    createUniqueStringColumn<IVariantSummary>({
      colId: ProductTableColumn.Gender,
      field: "gender",
      tooltipTitle: "Genders",
    }),
}

export const FABRIC_COLUMN: IdentitySpec = {
  colId: ProductTableColumn.Fabric,
  colDef: () =>
    createUniqueStringColumn<IVariantSummary>({
      colId: ProductTableColumn.Fabric,
      field: "fabric",
      tooltipTitle: "Fabrics",
    }),
}

export const PRODUCT_TYPE_COLUMN: IdentitySpec = {
  colId: ProductTableColumn.ProductType,
  calculate: (row) =>
    row.settings.productType ? translateProductType(row.settings.productType) : "",
  colDef: (ctx) =>
    createUniqueStringColumn<IVariantSummary>({
      colId: ProductTableColumn.ProductType,
      // Only allow filter when the filters apply on leaf nodes (variants)
      filter: ctx.groupAggFiltering === false ? true : undefined,
      tooltipTitle: "Product types",
    }),
}

export const TAGS_COLUMN: IdentitySpec = {
  colId: ProductTableColumn.Tags,
  render: (params) => {
    const entries: IVariantSummary[] = extractVariantSummaries(params)
    return <TagsCell entries={entries} value={params.value} node={params.node} />
  },
  colDef: () =>
    createUniqueStringColumn<IVariantSummary>({
      colId: ProductTableColumn.Tags,
      field: "tags",
      sortingOrder: ["desc", "asc", null],
      hasTooltip: true,
      tooltipComponentParams: { tooltipType: "Tags" },
      tooltip: (params) => {
        const entries: IVariantSummary[] = extractVariantSummaries(params)
        const tags = uniqBy(
          filterDefined(entries.flatMap((entry) => entry.tags ?? [])),
          (x) => x.id,
        )
        return { entries, tags }
      },
      // Order-insensitive: refetched tags come back reordered; default isEqual would
      // treat that as a change and flash this autoHeight cell on unrelated updates.
      equals: (a?: Tag[] | null, b?: Tag[] | null) => {
        const byId = (t?: Tag[] | null) =>
          [...(t ?? [])].sort((p, q) => (p.id < q.id ? -1 : p.id > q.id ? 1 : 0))
        return isEqual(byId(a), byId(b))
      },
      autoHeight: true,
      minWidth: 175,
    }),
}

export const PLANNER_COLUMN: IdentitySpec = {
  colId: ProductTableColumn.Planner,
  render: (params) => {
    if (Array.isArray(params.value) && params.value.length > 1) {
      return (
        <div className="flex items-center gap-2">
          <span>
            {params.value.length} {formatName("planner", true, true)}
          </span>
        </div>
      )
    }
    const email = Array.isArray(params.value) ? params.value[0] : params.value
    if (!email) return null
    return <CellUser userId={email} />
  },
  colDef: () =>
    createUniqueStringColumn<IVariantSummary>({
      colId: ProductTableColumn.Planner,
      hasTooltip: false,
      valueGetter: (params) => params.data?.settings?.userId ?? "Unassigned",
    }),
}

export const STOCK_STATUS_COLUMN: IdentitySpec = {
  colId: ProductTableColumn.StockStatus,
  render: (params) => {
    if (params.node?.group && Array.isArray(params.value) && params.value.length > 1) {
      return <div>{`${params.value.length} statuses`}</div>
    } else {
      const status = Array.isArray(params.value) ? first(params.value) : params.value
      return (
        <div className="h-fit py-2.5">
          <StatusBadge text={stockStatusText(status)} color={stockStatusColor(status)} />
        </div>
      )
    }
  },
  colDef: () =>
    createUniqueStringColumn<IVariantSummary>({
      colId: ProductTableColumn.StockStatus,
      valueGetter: (params) =>
        calculateStockStatusOnAggregatedLevel(
          params.data?.outOfStockInformation?.flatMap((o) => o?.status ?? "noStatus") ?? [],
        ),
      tooltipTitle: "Stock Statuses",
      tooltip: (params) =>
        Array.isArray(params.value) && params.value.length > 1
          ? params.value.map((s) => stockStatusText(s))
          : stockStatusText(params.value),
    }),
}

export const ABC_COLUMN: IdentitySpec = {
  colId: ProductTableColumn.ABC,
  render: (params) =>
    params.value ? (
      <Badge key={params.value} className="h-[20px]" variant="systemTag">
        {params.value}
      </Badge>
    ) : (
      "-"
    ),
  colDef: () =>
    createStringColumn<IVariantSummary>({
      colId: ProductTableColumn.ABC,
      field: "abc",
      aggFunc: "unique",
    }),
}

/**
 * Not contiguous in the table: columns from the other domains sit between them, so each is
 * placed at its own position from a single resolved lookup.
 */
export const IDENTITY_COLUMN_SPECS: readonly IdentitySpec[] = [
  PRODUCT_NAME_COLUMN,
  PRODUCT_COLUMN,
  PRODUCT_ID_COLUMN,
  VARIANT_ID_COLUMN,
  VARIANT_NAME_COLUMN,
  VARIANT_COLUMN,
  SKU_COLUMN,
  TAGS_COLUMN,
  PLANNER_COLUMN,
  COLOR_COLUMN,
  BASE_COLOR_COLUMN,
  STOCK_STATUS_COLUMN,
  SIZE_COLUMN,
  SIZE_DIMENSION_1_COLUMN,
  SIZE_DIMENSION_2_COLUMN,
  SIZE_SPLIT_CONFIDENCE_COLUMN,
  BRAND_COLUMN,
  CATEGORY_COLUMN,
  GENDER_COLUMN,
  FABRIC_COLUMN,
  ABC_COLUMN,
  PRODUCT_TYPE_COLUMN,
]
